import { z } from 'zod';

// ============================================================
// Common
// ============================================================

export const errorResponseSchema = z.object({
	code: z.string().describe('Машиночитаемый код ошибки, e.g. "INSUFFICIENT_BALANCE", "VAULT_NOT_FOUND"'),
	message: z.string().describe('Человекочитаемое описание ошибки'),
	data: z.record(z.string(), z.unknown()).optional().describe('Дополнительные данные по контексту ошибки'),
	requestId: z.string().describe('UUID запроса для отладки'),
});

export const tokenAmountSchema = z.object({
	raw: z.string().describe('Сумма в минимальных единицах токена, e.g. "1500000000000000000"'),
});

// ============================================================
// Reusable primitives (поэтому валидация не дублируется в use-cases)
// ============================================================

// Строковое представление неотрицательного целого (для bigint-полей через JSON).
// Используется в input-схемах, где сервер делает `BigInt(value)` — невалидная строка
// иначе уронит use-case 500-кой.
export const bigintStringSchema = z.string().regex(/^\d+$/, 'must be non-negative integer string');

// 0x + 40 hex. Принимаем любой регистр (checksum/lowercase/mixed), нормализуем в lowercase —
// это каноническая форма в БД. Без нормализации `0xAbC...` и `0xabc...` создавали бы дублирующиеся
// записи в users/user_vault_balances и SQL-сравнения через `=` (case-sensitive в PG) промахивались.
// Применяется и к input (body/headers), и к output: legacy данные с checksum из БД серилизуются
// как lowercase, не падая 500-кой на zod-валидации ответа.
export const walletAddressSchema = z
	.string()
	.regex(/^0x[a-fA-F0-9]{40}$/, 'must be 0x + 40 hex chars')
	.transform((value) => value.toLowerCase());

// 6 uppercase alphanumeric. Алфавит генератора уже́ исключает I/O/0/1,
// но input — `[A-Z0-9]{6}` (либеральнее, не сломаем уже сохранённые коды).
export const referralCodeSchema = z.string().regex(/^[A-Z0-9]{6}$/, 'must be 6 uppercase alphanumeric chars');

// 0x + чётное кол-во hex-символов. Для tos signature personal_sign = 130 или 132 chars,
// но допускаем произвольную длину чтобы переиспользовать для других hex-сигнатур.
export const hexSignatureSchema = z.string().regex(/^0x[a-fA-F0-9]+$/, 'must be 0x + hex chars');

export const chainIdSchema = z.number().int().positive();
export const decimalsSchema = z.number().int().min(0).max(36);
export const unixSecondsSchema = z.number().int().nonnegative();
export const vaultIdSchema = z.string().min(1).max(64);
export const chainCodeSchema = z.string().regex(/^[a-z][a-z0-9_-]{0,19}$/, 'must be lowercase kebab/snake_case 1-20 chars');

export const chainCodeHeader = z.object({
	'x-rs-chain-code': chainCodeSchema.describe('Символьный код сети, e.g. "eth", "base"'),
});

export const walletBody = z.object({
	walletAddress: walletAddressSchema.describe('Адрес кошелька пользователя, e.g. "0x742d35Cc..."'),
});

// ============================================================
// General
// ============================================================

export const currencySchema = z.object({
	code: z.string().describe('ISO 4217 код валюты, e.g. "USD", "EUR"'),
	symbol: z.string().describe('Символ для отображения, e.g. "$", "€"'),
});

export const languageSchema = z.object({
	code: z.string().describe('ISO 639-1 код языка, e.g. "en", "ru"'),
	name: z.string().describe('Отображаемое название, e.g. "English", "Русский"'),
});

// Output-схемы валидируют ответ через fastify-type-provider-zod (fail-loud):
// если БД содержит мусор (например, индексер записал битый адрес), сервер ответит 500
// и в логах будет явно видно, что данные расходятся с контрактом — это намеренно.
export const chainSchema = z.object({
	type: z.literal('evm').describe('Тип сети'),
	name: z.string().describe('Отображаемое название, e.g. "Ethereum", "Base"'),
	id: chainIdSchema.describe('EVM chain id, e.g. 1, 8453'),
	code: chainCodeSchema.describe('Внутренний символьный код, e.g. "eth", "base"'),
	iconUrl: z.string().describe('URL иконки сети'),
	explorerUrl: z.string().describe('Базовый URL эксплорера, e.g. "https://etherscan.io"'),
	rpcUrl: z.string().describe('Публичный RPC endpoint — фронт строит сеть кошелька и читает chain через него'),
	nativeCoin: z.object({
		name: z.string().describe('Название нативной монеты, e.g. "Ether"'),
		symbol: z.string().describe('Тикер, e.g. "ETH"'),
		decimals: decimalsSchema.describe('Количество десятичных знаков, e.g. 18'),
	}),
});

// ============================================================
// Vaults
// ============================================================

export const vaultTokenSchema = z.object({
	name: z.string().describe('Полное название токена, e.g. "Ether", "Tether USD"'),
	symbol: z.string().describe('Тикер, e.g. "ETH", "USDT"'),
	address: walletAddressSchema.describe('Адрес контракта токена, "0x0000..." для нативного'),
	decimals: decimalsSchema.describe('Количество десятичных знаков, e.g. 18, 6'),
	iconUrl: z.string().describe('URL иконки токена'),
});

export const vaultSchema = z.object({
	id: vaultIdSchema.describe('Уникальный идентификатор vault, e.g. "eth-yield"'),
	name: z.string().describe('Отображаемое название, e.g. "ETH Yield"'),
	description: z.string().describe('Текстовое описание стратегии'),
	token: vaultTokenSchema,
	contract: z.object({
		address: walletAddressSchema.describe('Адрес контракта vault'),
		explorerUrl: z.string().describe('Ссылка в блокчейн-эксплорере'),
	}),
	apy: z.number().describe('APY за 90 дней, процент, e.g. 4.2 = 4.2%'),
	tvl: tokenAmountSchema.describe('TVL в токене'),
	minDeposit: tokenAmountSchema.describe('Минимальная сумма депозита'),
	minWithdraw: tokenAmountSchema.describe('Минимальная сумма вывода'),
	fees: z.object({
		management: z.number().describe('Годовая комиссия за управление, e.g. 2.0 = 2%'),
		performance: z.number().describe('Комиссия от yield, e.g. 20.0 = 20%'),
	}),
	redemptionPeriodDays: z.number().describe('Дней ожидания вывода после запроса'),
	audits: z.array(
		z.object({
			company: z.string().describe('Название аудитора'),
			url: z.string().describe('Ссылка на отчёт аудита'),
		}),
	),
	protocols: z.array(
		z.object({
			name: z.string().describe('Название протокола, e.g. "Aave V3"'),
			iconUrl: z.string().describe('URL иконки протокола'),
			allocationPercent: z.number().describe('Доля аллокации, e.g. 35.5 = 35.5%'),
			apy: z.number().describe('APY протокола, e.g. 3.8 = 3.8%'),
		}),
	),
});

// ============================================================
// Transactions
// ============================================================

export const transactionInputSchema = z.intersection(
	walletBody,
	z.discriminatedUnion('type', [
		z.object({
			type: z.literal('deposit'),
			data: z.object({
				vaultId: vaultIdSchema.describe('ID vault для депозита'),
				amount: bigintStringSchema.describe('Сумма в минимальных единицах токена'),
				referralCode: referralCodeSchema.optional().describe('Реферальный код, только для первого депозита'),
			}),
		}),
		z.object({
			type: z.literal('withdraw'),
			data: z.object({
				vaultId: vaultIdSchema.describe('ID vault для вывода'),
				amount: bigintStringSchema.describe('Сумма в минимальных единицах токена'),
			}),
		}),
		z.object({
			type: z.literal('claimWithdrawal'),
			data: z.object({
				vaultId: vaultIdSchema.describe('ID vault'),
				requestId: bigintStringSchema.describe('ID заявки на вывод'),
			}),
		}),
	]),
);

export const stepSignMessageSchema = z.object({
	type: z.literal('sign_message'),
	data: z.object({
		title: z.string().describe('UI-заголовок шага, e.g. "Accept Terms of Service"'),
		purpose: z.enum(['tos']).describe('Назначение подписи'),
		message: z.string().describe('Текст для подписи кошельком. Содержит tosHash внутри текста'),
	}),
});

export const stepSendTransactionSchema = z.object({
	type: z.literal('send_transaction'),
	data: z.object({
		title: z.string().describe('UI-заголовок шага, e.g. "Approve USDC" / "Deposit ETH" / "Claim withdrawal"'),
		chainId: chainIdSchema.describe('EVM chain id для проверки/переключения сети'),
		to: walletAddressSchema.describe('Адрес контракта-получателя'),
		calldata: z.string().describe('Hex-encoded calldata'),
		value: z.string().describe('Нативный токен в wei (hex), "0x0" если не нужен'),
	}),
});

// ============================================================
// Portfolio
// ============================================================

export const withdrawalSchema = z.object({
	requestId: bigintStringSchema.describe('ID заявки для claimWithdrawal'),
	vaultId: vaultIdSchema.describe('ID vault'),
	name: z.string().describe('Название vault, e.g. "ETH Yield"'),
	tokenSymbol: z.string().describe('Тикер токена, e.g. "ETH"'),
	tokenIconUrl: z.string().describe('URL иконки токена'),
	createdAt: unixSecondsSchema.describe('Unix seconds, дата создания заявки'),
	amount: tokenAmountSchema.describe('Сумма вывода в токене'),
	amountUsd: z.string().describe('Сумма вывода в USD по текущему курсу'),
	status: z.enum(['pending', 'claimable']).describe('pending — ожидает, claimable — можно забрать'),
	readyAt: unixSecondsSchema.describe('Unix seconds, когда будет доступен вывод'),
});

export const portfolioVaultSchema = z.object({
	id: vaultIdSchema.describe('ID vault'),
	name: z.string().describe('Название vault, e.g. "ETH Yield"'),
	tokenSymbol: z.string().describe('Тикер токена'),
	tokenIconUrl: z.string().describe('URL иконки токена'),
	deposited: tokenAmountSchema.describe('Текущий депозит в токене'),
	depositedChange30d: z
		.number()
		.nullable()
		.describe(
			'Изменение суммы депозита в USD за последние 30 дней (процент). null если в окне [today-30d, today) для vault нет snapshot',
		),
	earnedRewards: tokenAmountSchema.describe('Награды за всё время в токене'),
	earnedRewards30d: tokenAmountSchema
		.nullable()
		.describe('Награды за последние 30 дней в токене. null если в окне [today-30d, today) для vault нет snapshot'),
	apy: z.number().describe('Текущий APY vault, процент'),
	firstDepositAt: unixSecondsSchema.describe('Unix seconds, дата первого депозита'),
});

export const portfolioTransactionSchema = z.object({
	type: z.enum(['deposit', 'withdraw']).describe('Тип операции'),
	status: z.enum(['success', 'pending', 'claimed']).describe('Статус: deposit→success, withdraw→pending/claimed'),
	date: unixSecondsSchema.describe('Unix seconds, дата транзакции'),
	amount: tokenAmountSchema.describe('Сумма в токене'),
	amountUsd: z.string().nullable().describe('Сумма в USD, null для pending withdraw'),
	tx: z.object({
		hash: z.string().describe('Хэш транзакции. Для withdraw: tx клейма если status=claimed, иначе tx заявки'),
		explorerUrl: z.string().describe('Ссылка в блокчейн-эксплорере'),
	}),
});

// ============================================================
// Profile
// ============================================================

export const tierSchema = z.object({
	name: z.string().describe('Название тира, e.g. "Silver"'),
	multiplier: z.number().describe('Множитель тира, e.g. 1.5'),
	isMax: z.boolean().describe('true если максимальный тир'),
});

// ============================================================
// Signature
// ============================================================

export const signatureSubmitSchema = z.intersection(
	walletBody.extend({
		message: z.string().min(1).describe('Текст сообщения, который был подписан (из шага sign_message)'),
		signature: hexSignatureSchema.describe('Hex-encoded подпись сообщения кошельком (результат personal_sign)'),
	}),
	z.discriminatedUnion('purpose', [
		z.object({
			purpose: z.literal('tos'),
		}),
	]),
);

// ============================================================
// Inferred types
// ============================================================

export type ErrorResponse = z.infer<typeof errorResponseSchema>;
export type TokenAmount = z.infer<typeof tokenAmountSchema>;
export type Currency = z.infer<typeof currencySchema>;
export type Language = z.infer<typeof languageSchema>;
export type Chain = z.infer<typeof chainSchema>;
export type Vault = z.infer<typeof vaultSchema>;
export type VaultToken = z.infer<typeof vaultTokenSchema>;
export type Withdrawal = z.infer<typeof withdrawalSchema>;
export type PortfolioVault = z.infer<typeof portfolioVaultSchema>;
export type PortfolioTransaction = z.infer<typeof portfolioTransactionSchema>;
